import { GoogleMap, Polygon, DrawingManagerF, useLoadScript } from "@react-google-maps/api";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { authFetch } from "../api";
import Header from "../components/Header";
import { useNavigate } from "react-router-dom";

const libraries = ["drawing", "geometry"];
const mapContainerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: 40.7167, lng: -73.956 };

const ZONE_COLORS = [
  "#FF0000", "#0066FF", "#00CC00", "#FF9900", "#9900CC",
  "#00CCCC", "#FF3399", "#666600", "#003366", "#CC0066"
];

const BOUNDARY_COLORS = [
  "#e53935", "#1e88e5", "#43a047", "#fb8c00", "#8e24aa",
  "#00acc1", "#d81b60", "#6d4c41", "#546e7a", "#f4511e"
];

export default function ZonesPage() {
  const navigate = useNavigate();
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) navigate("/login");
  }, [navigate]);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries
  });

  const mapRef = useRef(null);
  const polygonRefs = useRef({});
  const unzonedMarkersRef = useRef([]);
  const dropdownRef = useRef(null);

  // Core state
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState([]);
  const [allZones, setAllZones] = useState({}); // { nid: zones[] }
  const [unzonedCount, setUnzonedCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [boundariesOnly, setBoundariesOnly] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [hoodFilter, setHoodFilter] = useState("");
  const hoodFilterRef = useRef(null);

  // Derived: single neighborhood for CRUD mode
  const selectedNeighborhood = !boundariesOnly && selectedNeighborhoods.length === 1 ? selectedNeighborhoods[0] : null;
  const zones = selectedNeighborhood ? (allZones[selectedNeighborhood] || []) : [];
  const boundary = (() => {
    if (selectedNeighborhood) {
      const hood = neighborhoods.find(n => n.id === selectedNeighborhood);
      return hood?.boundary || null;
    }
    return null;
  })();

  // Flat zone list for multi-hood rendering
  const allZonesList = useMemo(() => {
    const list = [];
    let idx = 0;
    selectedNeighborhoods.forEach(nid => {
      (allZones[nid] || []).forEach(zone => {
        list.push({ ...zone, neighborhoodId: nid, colorIdx: idx });
        idx++;
      });
    });
    return list;
  }, [selectedNeighborhoods, allZones]);

  // Edit/create state
  const [editingZone, setEditingZone] = useState(null);
  const [editForm, setEditForm] = useState({ zone_name: "", base_score: 0, description: "" });
  const [editPolygons, setEditPolygons] = useState([]);
  const [creatingZone, setCreatingZone] = useState(false);
  const [newForm, setNewForm] = useState({ zone_name: "", base_score: 50, description: "" });
  const [drawnPolygons, setDrawnPolygons] = useState([]);
  const [drawingMode, setDrawingMode] = useState(false);

  // Boundary state
  const [editingBoundary, setEditingBoundary] = useState(false);
  const [drawingBoundary, setDrawingBoundary] = useState(false);
  const boundaryPolyRef = useRef(null);

  // Landmarks state
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [landmarks, setLandmarks] = useState([]);
  const [editingLandmark, setEditingLandmark] = useState(null); // landmark object or null
  const [landmarkForm, setLandmarkForm] = useState({ landmark_name: "", landmark_type: "positive", weight: 1.0, description: "", lat: "", lng: "" });
  const [addingLandmark, setAddingLandmark] = useState(false); // show add form
  const [placingLandmark, setPlacingLandmark] = useState(false); // click-on-map mode
  const landmarkMarkersRef = useRef([]);

  // Display toggles
  const [showUnzoned, setShowUnzoned] = useState(false);
  const [showZipCodes, setShowZipCodes] = useState(false);
  const [highlightedZone, setHighlightedZone] = useState(null);
  const [highlightedBoundary, setHighlightedBoundary] = useState(null);

  // Refs for drawn polygon google objects
  const drawnPolygonObjects = useRef([]);
  const zipLayerLoaded = useRef(false);
  const tooltipRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
        setHoodFilter("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Focus filter input when dropdown opens
  useEffect(() => {
    if (dropdownOpen && hoodFilterRef.current) hoodFilterRef.current.focus();
    if (!dropdownOpen) setHoodFilter("");
  }, [dropdownOpen]);

  // Load neighborhoods
  useEffect(() => {
    authFetch("/zones/neighborhoods")
      .then(data => setNeighborhoods(data.neighborhoods || []))
      .catch(() => {});
  }, []);

  // Load zones when neighborhoods change
  const loadAllZones = useCallback(async (nids) => {
    if (nids.length === 0) { setAllZones({}); return; }
    setLoading(true);
    const result = {};
    await Promise.all(nids.map(async nid => {
      try {
        const data = await authFetch(`/zones/${nid}`);
        result[nid] = data.zones || [];
      } catch { result[nid] = []; }
    }));
    setAllZones(result);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (selectedNeighborhoods.length > 0) {
      loadAllZones(selectedNeighborhoods);
    } else {
      setAllZones({});
    }
    // Clear editing state
    setEditingZone(null);
    setCreatingZone(false);
    setDrawingMode(false);
    setEditingBoundary(false);
    setDrawingBoundary(false);
    clearUnzonedMarkers();
    setShowUnzoned(false);
    setUnzonedCount(0);
  }, [selectedNeighborhoods, loadAllZones]);

  // Fit map bounds to selected neighborhoods
  useEffect(() => {
    if (!mapRef.current || !isLoaded || selectedNeighborhoods.length === 0) return;
    const bounds = new window.google.maps.LatLngBounds();
    let hasPoints = false;

    selectedNeighborhoods.forEach(nid => {
      const hood = neighborhoods.find(n => n.id === nid);
      if (hood?.boundary && hood.boundary.length >= 3) {
        hood.boundary.forEach(c => {
          bounds.extend({ lat: c[0], lng: c[1] });
          hasPoints = true;
        });
      }
    });

    if (!hasPoints) {
      Object.values(allZones).forEach(zoneList => {
        zoneList.forEach(z => z.polygons.forEach(p => p.coords.forEach(c => {
          bounds.extend({ lat: c[0], lng: c[1] });
          hasPoints = true;
        })));
      });
    }

    if (hasPoints) mapRef.current.fitBounds(bounds, 50);
  }, [selectedNeighborhoods, neighborhoods, allZones, isLoaded]);

  // Convert coords array to Google Maps path
  const toPath = (coords) => coords.map(c => ({ lat: c[0], lng: c[1] }));

  // Toggle a neighborhood in/out of selection
  const toggleNeighborhood = (nid) => {
    setSelectedNeighborhoods(prev =>
      prev.includes(nid) ? prev.filter(id => id !== nid) : [...prev, nid]
    );
  };

  // --- Zip code overlay ---

  const setupZipLayer = useCallback((map) => {
    map.data.loadGeoJson("/nyc-zip-codes.geojson");
    map.data.setStyle({
      visible: true,
      fillColor: "transparent",
      fillOpacity: 0,
      strokeColor: "#1a73e8",
      strokeWeight: 1.5,
      strokeOpacity: 0.6,
    });
    if (!tooltipRef.current) {
      const tooltip = document.createElement("div");
      tooltip.style.cssText = "position:fixed;padding:4px 8px;background:#333;color:white;border-radius:4px;font-size:12px;pointer-events:none;z-index:9999;display:none;font-family:sans-serif;";
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;
    }
    const tooltip = tooltipRef.current;
    map.data.addListener("mouseover", (event) => {
      map.data.overrideStyle(event.feature, { fillColor: "#1a73e8", fillOpacity: 0.1, strokeWeight: 2.5, strokeOpacity: 1 });
      const label = event.feature.getProperty("label") || event.feature.getProperty("modzcta");
      if (label) { tooltip.textContent = label; tooltip.style.display = "block"; }
    });
    map.data.addListener("mousemove", (event) => {
      tooltip.style.left = (event.domEvent?.clientX || 0) + 12 + "px";
      tooltip.style.top = (event.domEvent?.clientY || 0) + 12 + "px";
    });
    map.data.addListener("mouseout", (event) => {
      map.data.revertStyle(event.feature);
      tooltip.style.display = "none";
    });
  }, []);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (showZipCodes) {
      setupZipLayer(map);
      zipLayerLoaded.current = true;
    }
  }, [showZipCodes, setupZipLayer]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    if (showZipCodes && !zipLayerLoaded.current) {
      setupZipLayer(map);
      zipLayerLoaded.current = true;
    } else if (zipLayerLoaded.current) {
      map.data.setStyle(() => ({
        fillColor: "transparent",
        fillOpacity: 0,
        strokeColor: "#1a73e8",
        strokeWeight: 1.5,
        strokeOpacity: 0.6,
        visible: showZipCodes,
      }));
    }
  }, [showZipCodes, setupZipLayer]);

  // --- Unzoned properties (imperative for performance) ---

  const clearUnzonedMarkers = () => {
    unzonedMarkersRef.current.forEach(m => m.setMap(null));
    unzonedMarkersRef.current = [];
  };

  const loadUnzoned = useCallback(async (nid) => {
    if (!nid || !mapRef.current) return;
    try {
      const data = await authFetch(`/zones/${nid}/unzoned`);
      setUnzonedCount(data.count || 0);
      const markers = (data.properties || []).map(p => {
        return new window.google.maps.Circle({
          center: { lat: p.lat, lng: p.lng },
          radius: 15,
          fillColor: "#f44336",
          fillOpacity: 0.6,
          strokeColor: "#c62828",
          strokeWeight: 0.5,
          map: mapRef.current,
          clickable: false
        });
      });
      unzonedMarkersRef.current = markers;
    } catch {
      setUnzonedCount(0);
    }
  }, []);

  const handleToggleUnzoned = async (checked) => {
    setShowUnzoned(checked);
    if (checked && selectedNeighborhood) {
      await loadUnzoned(selectedNeighborhood);
    } else {
      clearUnzonedMarkers();
    }
  };

  // --- Landmark helpers ---

  const LANDMARK_COLORS = { positive: "#2e7d32", neutral: "#757575", negative: "#c62828" };

  const loadLandmarks = async (nid) => {
    try {
      const data = await authFetch(`/zones/${nid}/landmarks`);
      setLandmarks(data.landmarks || []);
    } catch { setLandmarks([]); }
  };

  const clearLandmarkMarkers = () => {
    landmarkMarkersRef.current.forEach(m => m.setMap(null));
    landmarkMarkersRef.current = [];
  };

  const renderLandmarkMarkers = (lms) => {
    clearLandmarkMarkers();
    if (!mapRef.current || !lms.length) return;
    lms.forEach(lm => {
      if (!lm.lat || !lm.lng) return;
      const color = LANDMARK_COLORS[lm.landmark_type] || "#757575";
      const marker = new window.google.maps.Marker({
        position: { lat: lm.lat, lng: lm.lng },
        map: mapRef.current,
        title: lm.landmark_name,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 8,
          fillColor: color,
          fillOpacity: 0.9,
          strokeColor: "#fff",
          strokeWeight: 2,
        },
        label: { text: lm.landmark_name?.[0]?.toUpperCase() || "?", color: "#fff", fontSize: "10px", fontWeight: "bold" },
      });
      marker.addListener("click", () => {
        handleStartEditLandmark(lm);
      });
      landmarkMarkersRef.current.push(marker);
    });
  };

  // Toggle landmarks
  const handleToggleLandmarks = async (checked) => {
    setShowLandmarks(checked);
    if (checked && selectedNeighborhood) {
      await loadLandmarks(selectedNeighborhood);
    } else {
      clearLandmarkMarkers();
      setLandmarks([]);
      setEditingLandmark(null);
      setAddingLandmark(false);
      setPlacingLandmark(false);
    }
  };

  // Render markers when landmarks change
  useEffect(() => {
    if (showLandmarks) renderLandmarkMarkers(landmarks);
    // eslint-disable-next-line
  }, [landmarks, showLandmarks]);

  // Reload landmarks when neighborhood changes
  useEffect(() => {
    if (showLandmarks && selectedNeighborhood) {
      loadLandmarks(selectedNeighborhood);
    } else {
      clearLandmarkMarkers();
      setLandmarks([]);
    }
    // eslint-disable-next-line
  }, [selectedNeighborhood]);

  const handleStartAddLandmark = () => {
    setAddingLandmark(true);
    setEditingLandmark(null);
    setLandmarkForm({ landmark_name: "", landmark_type: "positive", weight: 1.0, description: "", lat: "", lng: "" });
    setPlacingLandmark(true);
  };

  const handleStartEditLandmark = (lm) => {
    setEditingLandmark(lm);
    setAddingLandmark(false);
    setPlacingLandmark(false);
    setLandmarkForm({
      landmark_name: lm.landmark_name || "",
      landmark_type: lm.landmark_type || "positive",
      weight: lm.weight ?? 1.0,
      description: lm.description || "",
      lat: lm.lat || "",
      lng: lm.lng || "",
    });
    // Pan to landmark and bounce its marker
    if (mapRef.current && lm.lat && lm.lng) {
      mapRef.current.panTo({ lat: lm.lat, lng: lm.lng });
      const marker = landmarkMarkersRef.current.find(m => {
        const pos = m.getPosition();
        return Math.abs(pos.lat() - lm.lat) < 0.0001 && Math.abs(pos.lng() - lm.lng) < 0.0001;
      });
      if (marker) {
        marker.setAnimation(window.google.maps.Animation.BOUNCE);
        setTimeout(() => marker.setAnimation(null), 1500);
      }
    }
  };

  const handleSaveLandmark = async () => {
    if (!landmarkForm.landmark_name || !landmarkForm.lat || !landmarkForm.lng) {
      alert("Name and location are required.");
      return;
    }
    try {
      if (editingLandmark) {
        await authFetch(`/zones/landmarks/${editingLandmark.id}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            landmark_name: landmarkForm.landmark_name,
            landmark_type: landmarkForm.landmark_type,
            latitude: parseFloat(landmarkForm.lat),
            longitude: parseFloat(landmarkForm.lng),
            weight: parseFloat(landmarkForm.weight) || 1.0,
            description: landmarkForm.description || null,
          }),
        });
      } else {
        await authFetch(`/zones/${selectedNeighborhood}/landmarks`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            landmark_name: landmarkForm.landmark_name,
            landmark_type: landmarkForm.landmark_type,
            latitude: parseFloat(landmarkForm.lat),
            longitude: parseFloat(landmarkForm.lng),
            weight: parseFloat(landmarkForm.weight) || 1.0,
            description: landmarkForm.description || null,
          }),
        });
      }
      setEditingLandmark(null);
      setAddingLandmark(false);
      setPlacingLandmark(false);
      await loadLandmarks(selectedNeighborhood);
    } catch (e) {
      alert("Failed to save landmark: " + (e.message || e));
    }
  };

  const handleDeleteLandmark = async (id) => {
    if (!window.confirm("Delete this landmark?")) return;
    try {
      await authFetch(`/zones/landmarks/${id}`, { method: "DELETE" });
      setEditingLandmark(null);
      await loadLandmarks(selectedNeighborhood);
    } catch (e) {
      alert("Failed to delete: " + (e.message || e));
    }
  };

  const handleCancelLandmark = () => {
    setEditingLandmark(null);
    setAddingLandmark(false);
    setPlacingLandmark(false);
  };

  // --- CRUD handlers ---

  const handleCreate = async () => {
    if (!newForm.zone_name || drawnPolygons.length === 0) {
      alert("Enter a zone name and draw at least one polygon on the map.");
      return;
    }
    try {
      await authFetch(`/zones/${selectedNeighborhood}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zone_name: newForm.zone_name,
          base_score: parseInt(newForm.base_score) || 50,
          description: newForm.description || null,
          polygons: drawnPolygons.map(path => path.map(p => [p.lat, p.lng]))
        })
      });
      drawnPolygonObjects.current.forEach(p => p.setMap(null));
      drawnPolygonObjects.current = [];
      setCreatingZone(false);
      setDrawingMode(false);
      setDrawnPolygons([]);
      setNewForm({ zone_name: "", base_score: 50, description: "" });
      loadAllZones(selectedNeighborhoods);
    } catch (e) {
      alert("Failed to create zone: " + (e.message || e));
    }
  };

  const handleStartEdit = (zone) => {
    setEditingZone(zone.zone_name);
    setEditForm({
      zone_name: zone.zone_name,
      base_score: zone.base_score,
      description: zone.description || ""
    });
    setEditPolygons(zone.polygons.map(p => toPath(p.coords)));
    setCreatingZone(false);
    setDrawingMode(false);
  };

  const handleSaveEdit = async () => {
    try {
      const updatedPolygons = editPolygons.map((_, i) => {
        const ref = polygonRefs.current[`edit-${i}`];
        if (ref) {
          const path = ref.getPath();
          const coords = [];
          for (let j = 0; j < path.getLength(); j++) {
            const p = path.getAt(j);
            coords.push([p.lat(), p.lng()]);
          }
          return coords;
        }
        return editPolygons[i].map(p => [p.lat, p.lng]);
      });

      await authFetch(`/zones/${selectedNeighborhood}/${encodeURIComponent(editingZone)}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          zone_name: editForm.zone_name,
          base_score: parseInt(editForm.base_score) || 0,
          description: editForm.description || null,
          polygons: updatedPolygons
        })
      });
      setEditingZone(null);
      setEditPolygons([]);
      loadAllZones(selectedNeighborhoods);
    } catch (e) {
      alert("Failed to update zone: " + (e.message || e));
    }
  };

  const handleDelete = async (zoneName) => {
    if (!window.confirm(`Delete zone "${zoneName}"? This will soft-delete (set inactive).`)) return;
    try {
      await authFetch(`/zones/${selectedNeighborhood}/${encodeURIComponent(zoneName)}`, {
        method: "DELETE"
      });
      loadAllZones(selectedNeighborhoods);
    } catch (e) {
      alert("Failed to delete zone: " + (e.message || e));
    }
  };

  const handleCancelCreate = () => {
    drawnPolygonObjects.current.forEach(p => p.setMap(null));
    drawnPolygonObjects.current = [];
    setCreatingZone(false);
    setDrawingMode(false);
    setDrawnPolygons([]);
    setNewForm({ zone_name: "", base_score: 50, description: "" });
  };

  const handleCancelEdit = () => {
    setEditingZone(null);
    setEditPolygons([]);
  };

  // --- Boundary helpers ---

  const getBoundaryGooglePolygon = useCallback(() => {
    if (!boundary || boundary.length < 3) return null;
    return new window.google.maps.Polygon({
      paths: boundary.map(c => ({ lat: c[0], lng: c[1] }))
    });
  }, [boundary]);

  const isInsideBoundary = useCallback((latLng) => {
    const poly = getBoundaryGooglePolygon();
    if (!poly) return true;
    return window.google.maps.geometry.poly.containsLocation(latLng, poly);
  }, [getBoundaryGooglePolygon]);

  const allVerticesInBoundary = useCallback((coords) => {
    for (const c of coords) {
      const latLng = c.lat !== undefined
        ? new window.google.maps.LatLng(c.lat, c.lng)
        : new window.google.maps.LatLng(c[0], c[1]);
      if (!isInsideBoundary(latLng)) return false;
    }
    return true;
  }, [isInsideBoundary]);

  const handleSaveBoundary = async () => {
    const ref = boundaryPolyRef.current;
    if (!ref) return;
    const path = ref.getPath();
    const coords = [];
    for (let i = 0; i < path.getLength(); i++) {
      const p = path.getAt(i);
      coords.push([p.lat(), p.lng()]);
    }
    try {
      await authFetch(`/zones/neighborhoods/${selectedNeighborhood}/boundary`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ boundary: coords })
      });
      setEditingBoundary(false);
      setNeighborhoods(prev => prev.map(n =>
        n.id === selectedNeighborhood ? { ...n, boundary: coords } : n
      ));
    } catch (e) {
      alert("Failed to save boundary: " + (e.message || e));
    }
  };

  // Memoize drawing manager options
  const drawingManagerOptions = useMemo(() => ({
    drawingControl: false,
    polygonOptions: {
      fillColor: "#FF6600",
      fillOpacity: 0.3,
      strokeColor: "#FF6600",
      strokeWeight: 2,
      editable: true
    }
  }), []);

  if (!isLoaded) return <div style={{ padding: 40, textAlign: "center" }}>Loading Google Maps...</div>;

  // Build selected boundaries for rendering
  const selectedBoundaries = selectedNeighborhoods.map((nid, i) => {
    const hood = neighborhoods.find(n => n.id === nid);
    return {
      id: nid,
      name: hood?.name,
      boundary: hood?.boundary,
      color: BOUNDARY_COLORS[i % BOUNDARY_COLORS.length]
    };
  }).filter(b => b.boundary && b.boundary.length >= 3);

  const isMultiSelect = selectedNeighborhoods.length > 1;
  const isCrudMode = selectedNeighborhood && !boundariesOnly;

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh" }}>
      <Header title="Zone Management" showLogout />

      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 16px", borderBottom: "1px solid #ddd", background: "#f8f9fa", flexWrap: "wrap" }}>
        {/* Multi-select dropdown */}
        <div ref={dropdownRef} style={{ position: "relative" }}>
          <button
            onClick={() => setDropdownOpen(!dropdownOpen)}
            style={{
              padding: "6px 12px", borderRadius: 4, border: "1px solid #ccc", fontSize: 14,
              background: "#fff", cursor: "pointer", minWidth: 200, textAlign: "left",
              display: "flex", alignItems: "center", justifyContent: "space-between"
            }}
          >
            <span>
              {selectedNeighborhoods.length === 0
                ? "Select Neighborhoods"
                : selectedNeighborhoods.length === 1
                  ? neighborhoods.find(n => n.id === selectedNeighborhoods[0])?.name || "1 selected"
                  : `${selectedNeighborhoods.length} neighborhoods`}
            </span>
            <span style={{ fontSize: 10, marginLeft: 8 }}>{dropdownOpen ? "\u25B2" : "\u25BC"}</span>
          </button>
          {dropdownOpen && (
            <div style={{
              position: "absolute", top: "100%", left: 0, zIndex: 999, background: "#fff",
              border: "1px solid #ccc", borderRadius: 4, boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
              maxHeight: 350, overflowY: "auto", minWidth: 240, marginTop: 2
            }}>
              <div style={{ padding: "6px 10px", borderBottom: "1px solid #eee" }}>
                <input
                  ref={hoodFilterRef}
                  type="text"
                  value={hoodFilter}
                  onChange={(e) => setHoodFilter(e.target.value)}
                  placeholder="Type to filter..."
                  style={{ width: "100%", padding: "4px 8px", fontSize: 13, border: "1px solid #ddd", borderRadius: 3, boxSizing: "border-box", marginBottom: 4 }}
                />
                <div style={{ display: "flex", gap: 8 }}>
                  <button
                    onClick={() => setSelectedNeighborhoods(neighborhoods.map(n => n.id))}
                    style={{ fontSize: 12, color: "#1976d2", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    Select All
                  </button>
                  <button
                    onClick={() => setSelectedNeighborhoods([])}
                    style={{ fontSize: 12, color: "#999", background: "none", border: "none", cursor: "pointer", padding: 0 }}
                  >
                    Clear
                  </button>
                </div>
              </div>
              {neighborhoods.filter(n => !hoodFilter || n.name.toLowerCase().includes(hoodFilter.toLowerCase())).map(n => {
                const isSelected = selectedNeighborhoods.includes(n.id);
                const colorIdx = isSelected ? selectedNeighborhoods.indexOf(n.id) : -1;
                return (
                  <label
                    key={n.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "5px 10px",
                      cursor: "pointer", fontSize: 13, background: isSelected ? "#f0f7ff" : "transparent"
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = isSelected ? "#e3f0ff" : "#f5f5f5"}
                    onMouseLeave={e => e.currentTarget.style.background = isSelected ? "#f0f7ff" : "transparent"}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => toggleNeighborhood(n.id)}
                      style={{ margin: 0 }}
                    />
                    {isSelected && (
                      <div style={{
                        width: 10, height: 10, borderRadius: 2, flexShrink: 0,
                        background: BOUNDARY_COLORS[colorIdx % BOUNDARY_COLORS.length]
                      }} />
                    )}
                    <span>{n.name}</span>
                    {!n.boundary && <span style={{ fontSize: 10, color: "#ccc", marginLeft: "auto" }}>no boundary</span>}
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Zip codes for single selection */}
        {selectedNeighborhood && (() => {
          const hood = neighborhoods.find(n => n.id === selectedNeighborhood);
          return hood && hood.zip_codes && hood.zip_codes.length > 0 ? (
            <span style={{ fontSize: 13, color: "#666" }}>
              Zips: <strong>{hood.zip_codes.join(", ")}</strong>
            </span>
          ) : null;
        })()}

        {/* CRUD buttons - only in single-hood mode */}
        {isCrudMode && !creatingZone && !editingZone && !editingBoundary && !drawingBoundary && (
          <button
            onClick={() => { setCreatingZone(true); setDrawingMode(true); }}
            style={{ padding: "6px 14px", background: "#1976d2", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 }}
          >
            + New Zone
          </button>
        )}

        {isCrudMode && !creatingZone && !editingZone && !editingBoundary && !drawingBoundary && (
          <button
            onClick={() => {
              if (boundary && boundary.length >= 3) {
                setEditingBoundary(true);
              } else {
                setDrawingBoundary(true);
              }
            }}
            style={{ padding: "6px 14px", background: boundary ? "#555" : "#e65100", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 }}
          >
            {boundary ? "Edit Boundary" : "Draw Boundary"}
          </button>
        )}

        {(editingBoundary || drawingBoundary) && (
          <>
            {editingBoundary && (
              <button
                onClick={handleSaveBoundary}
                style={{ padding: "6px 14px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 }}
              >
                Save Boundary
              </button>
            )}
            <button
              onClick={() => {
                setEditingBoundary(false);
                setDrawingBoundary(false);
                const hood = neighborhoods.find(n => n.id === selectedNeighborhood);
                if (boundaryPolyRef.current?.setMap) boundaryPolyRef.current.setMap(null);
                boundaryPolyRef.current = null;
              }}
              style={{ padding: "6px 14px", background: "#999", color: "#fff", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 14 }}
            >
              Cancel
            </button>
            {drawingBoundary && (
              <span style={{ fontSize: 13, color: "#666" }}>Draw the neighborhood boundary on the map</span>
            )}
          </>
        )}

        {/* Right-side toggles */}
        <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={boundariesOnly}
              onChange={e => setBoundariesOnly(e.target.checked)}
            />
            Boundaries only
          </label>

          <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
            <input
              type="checkbox"
              checked={showZipCodes}
              onChange={e => setShowZipCodes(e.target.checked)}
            />
            Zip codes
          </label>

          {selectedNeighborhood && (
            <>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={showLandmarks}
                  onChange={e => handleToggleLandmarks(e.target.checked)}
                />
                Landmarks{landmarks.length > 0 ? ` (${landmarks.length})` : ""}
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={showUnzoned}
                  onChange={e => handleToggleUnzoned(e.target.checked)}
                />
                Unzoned{unzonedCount > 0 ? ` (${unzonedCount.toLocaleString()})` : ""}
              </label>
            </>
          )}
        </div>
      </div>

      <div style={{ display: "flex", flex: 1, overflow: "hidden" }}>
        {/* Sidebar */}
        <div style={{ width: 280, borderRight: "1px solid #ddd", overflowY: "auto", background: "#fff", padding: 12 }}>
          {selectedNeighborhoods.length === 0 && (
            <p style={{ color: "#999", fontSize: 13 }}>Select neighborhoods to view zones and boundaries.</p>
          )}

          {loading && <p style={{ color: "#999", fontSize: 13 }}>Loading zones...</p>}

          {/* Boundaries only mode */}
          {boundariesOnly && selectedNeighborhoods.length > 0 && (
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10, color: "#333" }}>Neighborhood Boundaries</div>
              {selectedNeighborhoods.map((nid, i) => {
                const hood = neighborhoods.find(n => n.id === nid);
                const color = BOUNDARY_COLORS[i % BOUNDARY_COLORS.length];
                const hasBoundary = hood?.boundary && hood.boundary.length >= 3;
                return (
                  <div
                    key={nid}
                    onMouseEnter={() => setHighlightedBoundary(nid)}
                    onMouseLeave={() => setHighlightedBoundary(null)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "8px 6px",
                      borderRadius: 4, cursor: "default",
                      background: highlightedBoundary === nid ? `${color}15` : "transparent",
                      transition: "background 0.15s"
                    }}
                  >
                    <div style={{
                      width: 24, height: 4, borderRadius: 2, flexShrink: 0,
                      background: hasBoundary ? color : "#ddd"
                    }} />
                    <span style={{ fontSize: 13, color: hasBoundary ? "#333" : "#bbb" }}>
                      {hood?.name}
                    </span>
                    {!hasBoundary && <span style={{ fontSize: 10, color: "#ccc", marginLeft: "auto" }}>no boundary</span>}
                  </div>
                );
              })}
            </div>
          )}

          {/* Single neighborhood CRUD mode */}
          {isCrudMode && !loading && (
            <>
              {/* Create zone form */}
              {creatingZone && (
                <div style={{ padding: 10, background: "#e3f2fd", borderRadius: 6, marginBottom: 12 }}>
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>New Zone</div>
                  <input
                    placeholder="Zone name"
                    value={newForm.zone_name}
                    onChange={e => setNewForm({ ...newForm, zone_name: e.target.value })}
                    style={{ width: "100%", padding: "5px 8px", marginBottom: 6, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                    <label style={{ fontSize: 12, minWidth: 40 }}>Score:</label>
                    <input
                      type="number" min={0} max={100}
                      value={newForm.base_score}
                      onChange={e => setNewForm({ ...newForm, base_score: e.target.value })}
                      style={{ width: 60, padding: "5px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 13 }}
                    />
                  </div>
                  <input
                    placeholder="Description (optional)"
                    value={newForm.description}
                    onChange={e => setNewForm({ ...newForm, description: e.target.value })}
                    style={{ width: "100%", padding: "5px 8px", marginBottom: 8, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                  />
                  <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                    {drawnPolygons.length === 0
                      ? "Click on the map to draw polygon vertices. Double-click to finish."
                      : `${drawnPolygons.length} polygon(s) drawn`}
                  </div>
                  {!drawingMode && drawnPolygons.length > 0 && (
                    <button
                      onClick={() => setDrawingMode(true)}
                      style={{ padding: "4px 10px", background: "#fff", border: "1px solid #1976d2", color: "#1976d2", borderRadius: 3, cursor: "pointer", fontSize: 12, marginBottom: 6, marginRight: 4 }}
                    >
                      + Add Polygon
                    </button>
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={handleCreate} style={{ padding: "5px 12px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 }}>Save</button>
                    <button onClick={handleCancelCreate} style={{ padding: "5px 12px", background: "#999", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                  </div>
                </div>
              )}

              {/* Zone list */}
              {zones.map((zone, zi) => {
                const color = ZONE_COLORS[zi % ZONE_COLORS.length];
                const isEditing = editingZone === zone.zone_name;
                const polyCount = zone.polygons.length;

                return (
                  <div
                    key={zone.zone_name}
                    onMouseEnter={() => setHighlightedZone(zone.zone_name)}
                    onMouseLeave={() => setHighlightedZone(null)}
                    style={{
                      padding: 10, marginBottom: 8, borderRadius: 6,
                      border: `2px solid ${highlightedZone === zone.zone_name ? color : "#eee"}`,
                      background: highlightedZone === zone.zone_name ? `${color}10` : "#fff",
                      transition: "border-color 0.15s, background 0.15s"
                    }}
                  >
                    {isEditing ? (
                      <>
                        <input
                          value={editForm.zone_name}
                          onChange={e => setEditForm({ ...editForm, zone_name: e.target.value })}
                          style={{ width: "100%", padding: "4px 8px", marginBottom: 6, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                        />
                        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 6 }}>
                          <label style={{ fontSize: 12, minWidth: 40 }}>Score:</label>
                          <input
                            type="number" min={0} max={100}
                            value={editForm.base_score}
                            onChange={e => setEditForm({ ...editForm, base_score: e.target.value })}
                            style={{ width: 60, padding: "4px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 13 }}
                          />
                        </div>
                        <input
                          value={editForm.description}
                          onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                          placeholder="Description"
                          style={{ width: "100%", padding: "4px 8px", marginBottom: 6, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                        />
                        <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
                          {editPolygons.length} polygon(s) — drag vertices on map to edit
                        </div>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={handleSaveEdit} style={{ padding: "4px 10px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Save</button>
                          <button onClick={handleCancelEdit} style={{ padding: "4px 10px", background: "#999", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Cancel</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
                          <div style={{ width: 14, height: 14, borderRadius: 3, background: color, flexShrink: 0 }} />
                          <span style={{ fontWeight: 600, fontSize: 14 }}>{zone.zone_name}</span>
                          <span style={{ marginLeft: "auto", fontSize: 12, color: "#666" }}>Score: {zone.base_score}</span>
                        </div>
                        {zone.description && (
                          <div style={{ fontSize: 12, color: "#888", marginBottom: 4 }}>{zone.description}</div>
                        )}
                        <div style={{ fontSize: 11, color: "#aaa", marginBottom: 6 }}>
                          {polyCount} polygon{polyCount !== 1 ? "s" : ""}
                        </div>
                        {!editingZone && !creatingZone && (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button onClick={() => handleStartEdit(zone)} style={{ padding: "3px 10px", background: "#fff", border: "1px solid #1976d2", color: "#1976d2", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Edit</button>
                            <button onClick={() => handleDelete(zone.zone_name)} style={{ padding: "3px 10px", background: "#fff", border: "1px solid #d32f2f", color: "#d32f2f", borderRadius: 3, cursor: "pointer", fontSize: 12 }}>Delete</button>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                );
              })}

              {!loading && zones.length === 0 && (
                <p style={{ color: "#999", fontSize: 13 }}>No zones configured for this neighborhood.</p>
              )}
            </>
          )}

          {/* Landmarks panel */}
          {showLandmarks && selectedNeighborhood && (
            <div style={{ borderTop: "1px solid #eee", paddingTop: 10, marginTop: 4 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 8 }}>
                <span style={{ fontWeight: 600, fontSize: 14, color: "#333" }}>Landmarks</span>
                {!addingLandmark && !editingLandmark && (
                  <button
                    onClick={handleStartAddLandmark}
                    style={{ padding: "3px 10px", background: "#fff", border: "1px solid #2e7d32", color: "#2e7d32", borderRadius: 3, cursor: "pointer", fontSize: 12 }}
                  >
                    + Add
                  </button>
                )}
              </div>

              {/* Add/Edit form */}
              {(addingLandmark || editingLandmark) && (
                <div style={{ padding: 10, background: editingLandmark ? "#fff8e1" : "#e8f5e9", borderRadius: 6, marginBottom: 10 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, fontSize: 13 }}>
                    {editingLandmark ? "Edit Landmark" : "New Landmark"}
                  </div>
                  <input
                    placeholder="Landmark name"
                    value={landmarkForm.landmark_name}
                    onChange={e => setLandmarkForm({ ...landmarkForm, landmark_name: e.target.value })}
                    style={{ width: "100%", padding: "5px 8px", marginBottom: 6, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, color: "#666" }}>Type</label>
                      <select
                        value={landmarkForm.landmark_type}
                        onChange={e => setLandmarkForm({ ...landmarkForm, landmark_type: e.target.value })}
                        style={{ width: "100%", padding: "5px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 13 }}
                      >
                        <option value="positive">Positive</option>
                        <option value="neutral">Neutral</option>
                        <option value="negative">Negative</option>
                      </select>
                    </div>
                    <div style={{ width: 70 }}>
                      <label style={{ fontSize: 11, color: "#666" }}>Weight</label>
                      <input
                        type="number" step="0.1" min="0" max="2"
                        value={landmarkForm.weight}
                        onChange={e => setLandmarkForm({ ...landmarkForm, weight: e.target.value })}
                        style={{ width: "100%", padding: "5px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                      />
                    </div>
                  </div>
                  <input
                    placeholder="Description (optional)"
                    value={landmarkForm.description}
                    onChange={e => setLandmarkForm({ ...landmarkForm, description: e.target.value })}
                    style={{ width: "100%", padding: "5px 8px", marginBottom: 6, borderRadius: 3, border: "1px solid #ccc", fontSize: 13, boxSizing: "border-box" }}
                  />
                  <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, color: "#666" }}>Lat</label>
                      <input
                        type="text"
                        value={landmarkForm.lat}
                        onChange={e => setLandmarkForm({ ...landmarkForm, lat: e.target.value })}
                        style={{ width: "100%", padding: "5px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }}
                        placeholder={placingLandmark ? "Click map..." : "40.7128"}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <label style={{ fontSize: 11, color: "#666" }}>Lng</label>
                      <input
                        type="text"
                        value={landmarkForm.lng}
                        onChange={e => setLandmarkForm({ ...landmarkForm, lng: e.target.value })}
                        style={{ width: "100%", padding: "5px 8px", borderRadius: 3, border: "1px solid #ccc", fontSize: 12, boxSizing: "border-box" }}
                        placeholder={placingLandmark ? "Click map..." : "-73.956"}
                      />
                    </div>
                    {!placingLandmark && (
                      <button
                        onClick={() => setPlacingLandmark(true)}
                        title="Pick location on map"
                        style={{ alignSelf: "flex-end", padding: "5px 8px", background: "#fff", border: "1px solid #ccc", borderRadius: 3, cursor: "pointer", fontSize: 16 }}
                      >
                        📍
                      </button>
                    )}
                  </div>
                  {placingLandmark && (
                    <div style={{ fontSize: 12, color: "#e65100", marginBottom: 6 }}>Click on the map to set location</div>
                  )}
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={handleSaveLandmark} style={{ padding: "5px 12px", background: "#2e7d32", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 }}>Save</button>
                    <button onClick={handleCancelLandmark} style={{ padding: "5px 12px", background: "#999", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13 }}>Cancel</button>
                    {editingLandmark && (
                      <button onClick={() => handleDeleteLandmark(editingLandmark.id)} style={{ padding: "5px 12px", background: "#d32f2f", color: "#fff", border: "none", borderRadius: 3, cursor: "pointer", fontSize: 13, marginLeft: "auto" }}>Delete</button>
                    )}
                  </div>
                </div>
              )}

              {/* Landmarks list */}
              {landmarks.length === 0 && !addingLandmark && (
                <p style={{ color: "#999", fontSize: 12 }}>No landmarks configured.</p>
              )}
              {landmarks.map(lm => {
                const color = LANDMARK_COLORS[lm.landmark_type] || "#757575";
                const isActive = editingLandmark?.id === lm.id;
                return (
                  <div
                    key={lm.id}
                    onClick={() => !addingLandmark && handleStartEditLandmark(lm)}
                    style={{
                      display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                      borderRadius: 4, cursor: "pointer", marginBottom: 2,
                      background: isActive ? `${color}15` : "transparent",
                      border: isActive ? `1px solid ${color}` : "1px solid transparent",
                      transition: "background 0.15s"
                    }}
                  >
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, flexShrink: 0 }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{lm.landmark_name}</div>
                      <div style={{ fontSize: 11, color: "#888" }}>{lm.landmark_type} · w:{lm.weight}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Multi-neighborhood view mode (not boundaries only) */}
          {!boundariesOnly && isMultiSelect && !loading && (
            <div>
              {selectedNeighborhoods.map((nid) => {
                const hood = neighborhoods.find(n => n.id === nid);
                const hoodZones = allZonesList.filter(z => z.neighborhoodId === nid);
                return (
                  <div key={nid} style={{ marginBottom: 14 }}>
                    <div style={{ fontWeight: 600, fontSize: 13, color: "#333", borderBottom: "1px solid #eee", paddingBottom: 4, marginBottom: 6 }}>
                      {hood?.name}
                    </div>
                    {hoodZones.length === 0 && (
                      <div style={{ fontSize: 12, color: "#bbb", paddingLeft: 4 }}>No zones</div>
                    )}
                    {hoodZones.map(zone => {
                      const color = ZONE_COLORS[zone.colorIdx % ZONE_COLORS.length];
                      return (
                        <div
                          key={zone.zone_name}
                          onMouseEnter={() => setHighlightedZone(`${nid}-${zone.zone_name}`)}
                          onMouseLeave={() => setHighlightedZone(null)}
                          style={{
                            display: "flex", alignItems: "center", gap: 6, padding: "4px 4px",
                            borderRadius: 3, cursor: "default",
                            background: highlightedZone === `${nid}-${zone.zone_name}` ? `${color}15` : "transparent"
                          }}
                        >
                          <div style={{ width: 12, height: 12, borderRadius: 2, background: color, flexShrink: 0 }} />
                          <span style={{ fontSize: 13 }}>{zone.zone_name}</span>
                          <span style={{ fontSize: 11, color: "#999", marginLeft: "auto" }}>{zone.base_score}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Map */}
        <div style={{ flex: 1 }}>
          <GoogleMap
            mapContainerStyle={mapContainerStyle}
            center={defaultCenter}
            zoom={14}
            onLoad={onMapLoad}
            onClick={(e) => {
              if (placingLandmark && e.latLng) {
                setLandmarkForm(f => ({ ...f, lat: e.latLng.lat().toFixed(6), lng: e.latLng.lng().toFixed(6) }));
                setPlacingLandmark(false);
              }
            }}
            options={{ mapTypeControl: true, streetViewControl: false, fullscreenControl: false }}
          >
            {/* === BOUNDARIES ONLY MODE === */}
            {boundariesOnly && selectedBoundaries.map(b => (
              <Polygon
                key={`boundary-${b.id}`}
                paths={b.boundary.map(c => ({ lat: c[0], lng: c[1] }))}
                options={{
                  fillColor: b.color,
                  fillOpacity: highlightedBoundary === b.id ? 0.15 : 0.05,
                  strokeColor: b.color,
                  strokeWeight: highlightedBoundary === b.id ? 4 : 2.5,
                  strokeOpacity: 0.9,
                  zIndex: highlightedBoundary === b.id ? 10 : 1
                }}
                onMouseOver={() => setHighlightedBoundary(b.id)}
                onMouseOut={() => setHighlightedBoundary(null)}
              />
            ))}

            {/* === SINGLE NEIGHBORHOOD MODE === */}
            {isCrudMode && (
              <>
                {/* Existing zone polygons (non-editing) */}
                {zones.map((zone, zi) => {
                  const color = ZONE_COLORS[zi % ZONE_COLORS.length];
                  if (editingZone === zone.zone_name) return null;
                  return zone.polygons.map((poly, pi) => (
                    <Polygon
                      key={`${zone.zone_name}-${pi}`}
                      paths={toPath(poly.coords)}
                      options={{
                        fillColor: color,
                        fillOpacity: highlightedZone === zone.zone_name ? 0.5 : 0.25,
                        strokeColor: color,
                        strokeWeight: highlightedZone === zone.zone_name ? 3 : 2,
                        strokeOpacity: 0.9
                      }}
                      onMouseOver={() => setHighlightedZone(zone.zone_name)}
                      onMouseOut={() => setHighlightedZone(null)}
                    />
                  ));
                })}

                {/* Editable polygons for zone being edited */}
                {editingZone && editPolygons.map((path, i) => (
                  <Polygon
                    key={`edit-${i}`}
                    paths={path}
                    options={{
                      fillColor: "#FF6600",
                      fillOpacity: 0.35,
                      strokeColor: "#FF6600",
                      strokeWeight: 3,
                      editable: true,
                      draggable: true
                    }}
                    onLoad={poly => { polygonRefs.current[`edit-${i}`] = poly; }}
                    onUnmount={() => { delete polygonRefs.current[`edit-${i}`]; }}
                  />
                ))}

                {/* Neighborhood boundary */}
                {boundary && boundary.length >= 3 && !editingBoundary && (
                  <Polygon
                    key="boundary-static"
                    paths={boundary.map(c => ({ lat: c[0], lng: c[1] }))}
                    options={{
                      fillColor: "transparent",
                      fillOpacity: 0,
                      strokeColor: "#333",
                      strokeWeight: 3,
                      strokeOpacity: 0.8,
                      clickable: false,
                      zIndex: 0
                    }}
                  />
                )}

                {/* Editable boundary polygon */}
                {editingBoundary && boundary && boundary.length >= 3 && (
                  <Polygon
                    key="boundary-editable"
                    paths={boundary.map(c => ({ lat: c[0], lng: c[1] }))}
                    options={{
                      fillColor: "#ff9800",
                      fillOpacity: 0.08,
                      strokeColor: "#e65100",
                      strokeWeight: 3,
                      editable: true,
                      draggable: false
                    }}
                    onLoad={poly => {
                      boundaryPolyRef.current = poly;
                      // Right-click vertex to delete it
                      poly.addListener('rightclick', (e) => {
                        if (e.vertex != null && poly.getPath().getLength() > 3) {
                          poly.getPath().removeAt(e.vertex);
                        }
                      });
                    }}
                    onUnmount={() => { boundaryPolyRef.current = null; }}
                  />
                )}
              </>
            )}

            {/* === MULTI-NEIGHBORHOOD MODE (not boundaries only) === */}
            {!boundariesOnly && isMultiSelect && (
              <>
                {/* All boundaries as thin gray lines */}
                {selectedBoundaries.map(b => (
                  <Polygon
                    key={`multi-boundary-${b.id}`}
                    paths={b.boundary.map(c => ({ lat: c[0], lng: c[1] }))}
                    options={{
                      fillColor: "transparent",
                      fillOpacity: 0,
                      strokeColor: "#333",
                      strokeWeight: 2,
                      strokeOpacity: 0.5,
                      clickable: false,
                      zIndex: 0
                    }}
                  />
                ))}

                {/* All zone polygons */}
                {allZonesList.map(zone => {
                  const color = ZONE_COLORS[zone.colorIdx % ZONE_COLORS.length];
                  const hoverKey = `${zone.neighborhoodId}-${zone.zone_name}`;
                  return zone.polygons.map((poly, pi) => (
                    <Polygon
                      key={`multi-${zone.neighborhoodId}-${zone.zone_name}-${pi}`}
                      paths={toPath(poly.coords)}
                      options={{
                        fillColor: color,
                        fillOpacity: highlightedZone === hoverKey ? 0.5 : 0.25,
                        strokeColor: color,
                        strokeWeight: highlightedZone === hoverKey ? 3 : 2,
                        strokeOpacity: 0.9
                      }}
                      onMouseOver={() => setHighlightedZone(hoverKey)}
                      onMouseOut={() => setHighlightedZone(null)}
                    />
                  ));
                })}
              </>
            )}

            {/* Drawing manager for creating new zones or boundaries */}
            {(drawingMode || drawingBoundary) && (
              <DrawingManagerF
                options={{
                  ...drawingManagerOptions,
                  drawingMode: window.google.maps.drawing.OverlayType.POLYGON
                }}
                onPolygonComplete={(polygon) => {
                  const path = polygon.getPath();
                  const coords = [];
                  for (let i = 0; i < path.getLength(); i++) {
                    const p = path.getAt(i);
                    coords.push({ lat: p.lat(), lng: p.lng() });
                  }

                  if (drawingBoundary) {
                    polygon.setOptions({
                      fillColor: "transparent",
                      fillOpacity: 0,
                      strokeColor: "#333",
                      strokeWeight: 3,
                      editable: true
                    });
                    boundaryPolyRef.current = polygon;
                    setDrawingBoundary(false);
                    setEditingBoundary(true);
                    return;
                  }

                  if (boundary && boundary.length >= 3 && !allVerticesInBoundary(coords)) {
                    alert("Zone polygon must be within the neighborhood boundary.");
                    polygon.setMap(null);
                    setDrawingMode(false);
                    return;
                  }

                  setDrawnPolygons(prev => [...prev, coords]);
                  drawnPolygonObjects.current.push(polygon);
                  polygon.setOptions({
                    fillColor: "#FF6600",
                    fillOpacity: 0.3,
                    strokeColor: "#FF6600",
                    strokeWeight: 2,
                    editable: true
                  });
                  setDrawingMode(false);
                }}
              />
            )}
          </GoogleMap>
        </div>
      </div>
    </div>
  );
}
